import React, { Fragment, useEffect, useState, forwardRef } from "react";
import {
  DataGrid,
  GridToolbar,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  LinearProgress,
  Snackbar,
  Typography,
  Tooltip,
  Dialog,
  DialogContent,
  IconButton,
  FormControlLabel,
  Checkbox,
  Box,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  MenuItem,
  Menu,
  DialogTitle,
  DialogActions,
  Paper,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer";
import NetLoadTooltipContent from "./NetLoadTooltipContent";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import {
  formatMemberElapsedTime,
  formatMemberElapsedTimeCompact,
} from "./memberElapsedTime";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MemberDetail from "./MemberDetail";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import EmailIcon from "@mui/icons-material/Email";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import BusinessIcon from "@mui/icons-material/Business";
import ManageCard from "./ManageCard";
import MemberTransactions from "./MemberTransactions";
import MemberStatements from "./MemberStatements";
import DescriptionIcon from "@mui/icons-material/Description";
import ModifyCompanyMember from "./ModifyCompanyMember";
import ModifyEmailMember from "./ModifyEmailMember";
import MemberSecureInfo from "./MemberSecureInfo";
import RenderOnRole from "./RenderOnRole";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import UserService from "../services/UserService";
import Labels from "../labels/Labels";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const NEUTRAL_STATUS_COLORS = [
  "#1565c0",
  "#0277bd",
  "#00838f",
  "#5e35b1",
  "#455a64",
  "#6d4c41",
  "#3949ab",
  "#78909c",
];
const PASSED_STATUS_COLOR = "#2e7d32";
const FAILED_STATUS_COLOR = "#c62828";
const REVIEW_STATUSES = [
  "MANUAL",
  "KYC_MANUAL",
  "KYC_MORE_INFO",
  "UNDER_REVIEW",
];

const ORDER_STATUS_ANALYTICS_ROLES = ["pc-program-super-admin", "compliance-viewer", "program-management"];

const DISTRIBUTION_PERIODS = [
  ["allTime", "All time"],
  ["thisMonth", "This month"],
  ["thisWeek", "This week"],
  ["today", "Today"],
];

const KPI_PERIODS = [
  ["allTime", "All time"],
  ["thisMonth", "This month"],
  ["thisWeek", "This week"],
  ["today", "Today"],
];

const MANUAL_STATUS_COLOR = "#ef6c00";
const NET_LOAD_COLOR = "#00838f";
const TOTAL_LOAD_BAR_COLOR = "rgba(0, 131, 143, 0.22)";
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function formatCurrency(value) {
  return currencyFormatter.format(Number(value || 0));
}

function withStore(url, storeId) {
  return storeId ? `${url}&storeId=${encodeURIComponent(storeId)}` : url;
}
const KPI_CHART_COLORS = {
  ofac: "#c62828",
  intervention: "#ef6c00",
  neither: "#2e7d32",
};

function groupStatisticStatuses(entries) {
  const counts = new Map();
  for (const entry of entries) {
    const rawStatus = entry.status || "UNKNOWN";
    const status = ["REQUESTED", "COMPLETED"].includes(rawStatus)
      ? "PASSED"
      : rawStatus;
    counts.set(status, (counts.get(status) || 0) + Number(entry.count || 0));
  }
  return Array.from(counts, ([status, count]) => ({ status, count })).sort(
    (a, b) => b.count - a.count
  );
}

function getStatusColor(status) {
  const normalized = String(status || "UNKNOWN")
    .trim()
    .toUpperCase();
  if (REVIEW_STATUSES.includes(normalized)) return MANUAL_STATUS_COLOR;
  if (normalized === "PASSED") return PASSED_STATUS_COLOR;
  if (/FAIL|REJECT|DENI|DECLIN/.test(normalized)) return FAILED_STATUS_COLOR;
  let hash = 0;
  for (const char of normalized) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return NEUTRAL_STATUS_COLORS[hash % NEUTRAL_STATUS_COLORS.length];
}

function formatKpiDuration(seconds) {
  return Number.isSafeInteger(seconds)
    ? formatMemberElapsedTimeCompact(seconds * 1000)
    : "N/A";
}

export default function Members(props) {
  const {
    isLoading,
    isFetching,
    isError,
    data,
    page,
    setPageFunction,
    pageSize = 10,
    pageSizeOptions = [10],
    setPageSizeFunction,
    setFilterStringFunction,
    setUnderReviewOnlyFunction,
    setEmployerFilterFunction,
    setStoreFilterFunction,
    setOrderStatusFilterFunction,
    setOrderSubStatusFilterFunction,
    dataCount,
    isLoadingDataCount,
    isFetchingDataCount,
    isErrorDataCount,
    dataUpdatedAt,
    countUpdatedAt,
    refreshInterval,
    automaticRefresh,
    setAutomaticRefreshFunction,
    title,
  } = props;
  const queryClient = useQueryClient();
  const refreshMembers = () => queryClient.refetchQueries({
    type: "active",
    predicate: (query) => [
      "members", "membersCount", "members-signups-hourly", "members-load-volume",
      "members-order-status-distribution-periods", "members-kyc-kpis",
      "members-kyc-manual-substatus-distribution", "members-store-distribution",
    ].includes(query.queryKey[0]),
  });

  // State for employer filter
  const [selectedEmployer, setSelectedEmployer] = useState(null);

  // Fetch all employers
  const { data: employersData, isLoading: isLoadingEmployers } = useQuery(
    ["employers"],
    () =>
      HttpService.getAxiosClient().get(
        window.API_URL + `/employer/all?excludeRelations=true`
      ),
    {
      staleTime: 5 * 60, // Cache for 5 minutes
    }
  );

  // Handle employer selection change
  const handleEmployerChange = (event, newValue) => {
    setSelectedEmployer(newValue);
    setSelectedStore("");
    setSelectedOrderStatus(null);
    setSelectedOrderPeriod("allTime");
    setSelectedOrderSubStatus(null);
    if (setEmployerFilterFunction) {
      // Employer objects use idEmployer as their identifier
      setEmployerFilterFunction(newValue?.idEmployer || null);
    }
  };

  const [select, setSelection] = useState([]);
  const [underReviewChecked, setUnderReviewChecked] = useState(false);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState(null);
  const [selectedOrderPeriod, setSelectedOrderPeriod] = useState("allTime");
  const [dayPeriod, setDayPeriod] = useState("today");
  const [dayMenuAnchor, setDayMenuAnchor] = useState(null);
  const [loadMonthDialogOpen, setLoadMonthDialogOpen] = useState(false);
  const [customLoadMonth, setCustomLoadMonth] = useState("");
  const [loadMonthError, setLoadMonthError] = useState(false);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [customDay, setCustomDay] = useState("");
  const [dayError, setDayError] = useState(false);
  const [includeEmptySignupDays, setIncludeEmptySignupDays] = useState(true);
  const [signupWeekday, setSignupWeekday] = useState(0);
  const [signupInfoOpen, setSignupInfoOpen] = useState(false);
  const [distributionInfoOpen, setDistributionInfoOpen] = useState(false);
  const [signupTimeZone, setSignupTimeZone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone === "America/Los_Angeles"
      ? "pacific" : "eastern"
  );
  const currentDate = new Date();
  const maxDay = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
  const maxMonth = maxDay.slice(0, 7);
  const dayTitle =
    dayPeriod === "today"
      ? Labels.getLabel("members.distribution.today", "Today")
      : dayPeriod === "yesterday"
        ? Labels.getLabel("members.distribution.yesterday", "Yesterday")
        : { last7Days: "Last 7 days", lastMonth: "Last month" }[dayPeriod] || dayPeriod;
  const creationPeriod =
    selectedOrderPeriod === "today" ? dayPeriod : selectedOrderPeriod;
  const loadPeriod = creationPeriod;
  const [selectedOrderSubStatus, setSelectedOrderSubStatus] = useState(null);
  const [analyticsView, setAnalyticsView] = useState("distribution");
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
  const [selectedStore, setSelectedStore] = useState("");
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  async function fetchMembersOrderStatusDistribution(
    employerId,
    underReviewOnly,
    storeId,
    day
  ) {
    let url = withStore(
      window.API_URL +
        `/pad/members/status-distribution/periods?underReviewOnly=${underReviewOnly}`,
      storeId
    );

    if (employerId) {
      url += `&employerId=${encodeURIComponent(employerId)}`;
    }
    if (day !== "today") {
      url += `&dayPeriod=${encodeURIComponent(day)}`;
    }

    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  async function fetchKycManualSubStatusDistribution(
    employerId,
    underReviewOnly,
    storeId,
    creationPeriod
  ) {
    let url = withStore(
      window.API_URL +
        `/pad/members/status-distribution/kyc-manual?underReviewOnly=${underReviewOnly}`,
      storeId
    );

    if (employerId) {
      url += `&employerId=${encodeURIComponent(employerId)}`;
    }
    if (creationPeriod !== "allTime") {
      url += `&creationPeriod=${encodeURIComponent(creationPeriod)}`;
    }

    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  async function fetchMemberKpis(employerId, underReviewOnly, storeId, day) {
    let url = withStore(
      window.API_URL +
        `/pad/members/kyc-kpis?underReviewOnly=${underReviewOnly}`,
      storeId
    );
    if (employerId) {
      url += `&employerId=${encodeURIComponent(employerId)}`;
    }
    if (day !== "today") {
      url += `&dayPeriod=${encodeURIComponent(day)}`;
    }
    return HttpService.getAxiosClient().get(url);
  }

  async function fetchMemberStores(employerId) {
    let url = window.API_URL + `/pad/members/stores`;
    if (employerId) {
      url += `?employerId=${encodeURIComponent(employerId)}`;
    }
    return HttpService.getAxiosClient().get(url);
  }

  async function fetchMemberStoreDistribution(employerId, underReviewOnly, day) {
    let url =
      window.API_URL +
      `/pad/members/status-distribution/stores?underReviewOnly=${underReviewOnly}`;
    if (employerId) {
      url += `&employerId=${encodeURIComponent(employerId)}`;
    }
    if (day !== "today") {
      url += `&dayPeriod=${encodeURIComponent(day)}`;
    }
    return HttpService.getAxiosClient().get(url);
  }

  async function fetchLoadVolume(employerId, storeId, period) {
    const params = new URLSearchParams();
    params.set("period", period);
    if (storeId) {
      params.set("storeId", storeId);
    }
    if (employerId) {
      params.set("employerId", employerId);
    }
    const query = params.toString();
    return HttpService.getAxiosClient().get(
      window.API_URL +
        `/pad/members/load-volume?${query}`
    );
  }

  const effectiveEmployerId = selectedEmployer?.idEmployer || null;
  const effectiveStoreId = selectedStore || null;
  const canViewOrderStatusAnalytics = UserService.hasRole(
    ORDER_STATUS_ANALYTICS_ROLES
  );
  const canViewManagementAnalytics = canViewOrderStatusAnalytics &&
    UserService.hasRole(["program-management"]);
  const canViewSignupAndKpiAnalytics = canViewOrderStatusAnalytics &&
    UserService.hasRole(["program-management", "compliance-viewer"]);

  useEffect(() => {
    if ((analyticsView === "totalLoad" && !canViewManagementAnalytics) ||
        (["last7Days", "hourlySignups"].includes(analyticsView) &&
          !canViewManagementAnalytics) ||
        (analyticsView === "kpis" && !canViewSignupAndKpiAnalytics)) {
      setAnalyticsView("distribution");
    }
  }, [canViewManagementAnalytics, canViewSignupAndKpiAnalytics, analyticsView]);

  const { data: storesData, isLoading: isLoadingStores } = useQuery(
    ["member-stores", effectiveEmployerId],
    () => fetchMemberStores(effectiveEmployerId),
    { staleTime: 5 * 60 * 1000 }
  );
  const storeOptions = (Array.isArray(storesData?.data) ? storesData.data : [])
    .filter((store) => typeof store === "string" || typeof store === "number")
    .map(String);

  const selectStore = (storeId) => {
    setSelectedStore(storeId);
    if (setStoreFilterFunction) {
      setStoreFilterFunction(storeId || null);
    }
  };

  const handleStoreChange = (event) => {
    selectStore(event.target.value || "");
  };

  const isTotalLoadVisible =
    canViewManagementAnalytics && analyticsView === "totalLoad";
  const isHourlySignupsVisible =
    canViewManagementAnalytics && analyticsView === "hourlySignups";
  const {
    data: hourlySignupsData,
    isLoading: isLoadingHourlySignups,
    isFetching: isFetchingHourlySignups,
    isError: isErrorHourlySignups,
    dataUpdatedAt: hourlySignupsUpdatedAt,
  } = useQuery(
    ["members-signups-hourly", effectiveEmployerId, effectiveStoreId, underReviewChecked, creationPeriod],
    () => {
      const params = new URLSearchParams({
        period: creationPeriod,
        underReviewOnly: String(underReviewChecked),
      });
      if (effectiveStoreId) params.set("storeId", effectiveStoreId);
      if (effectiveEmployerId) params.set("employerId", effectiveEmployerId);
      return HttpService.getAxiosClient().get(
        window.API_URL + `/pad/members/signups/hourly?${params.toString()}`
      );
    },
    {
      staleTime: 60 * 1000,
      enabled: isHourlySignupsVisible,
      refetchInterval: refreshInterval,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );
  const {
    data: netLoadData,
    isLoading: isLoadingNetLoad,
    isFetching: isFetchingNetLoad,
    isError: isErrorNetLoad,
    dataUpdatedAt: netLoadUpdatedAt,
  } = useQuery(
    ["members-load-volume", effectiveEmployerId, effectiveStoreId, loadPeriod],
    () => fetchLoadVolume(effectiveEmployerId, effectiveStoreId, loadPeriod),
    {
      staleTime: 60 * 1000,
      enabled: isTotalLoadVisible,
      refetchInterval: refreshInterval,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const {
    data: orderStatusDistributionData,
    isLoading: isLoadingOrderStatusDistribution,
    isFetching: isFetchingOrderStatusDistribution,
    isError: isErrorOrderStatusDistribution,
    dataUpdatedAt: distributionUpdatedAt,
  } = useQuery(
    [
      "members-order-status-distribution-periods",
      effectiveEmployerId,
      underReviewChecked,
      effectiveStoreId,
      dayPeriod,
    ],
    () =>
      fetchMembersOrderStatusDistribution(
        effectiveEmployerId,
        underReviewChecked,
        effectiveStoreId,
        dayPeriod
      ),
    {
      staleTime: 60 * 1000,
      enabled: canViewOrderStatusAnalytics,
      refetchInterval: refreshInterval,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const {
    data: memberKpisData,
    isLoading: isLoadingMemberKpis,
    isFetching: isFetchingMemberKpis,
    isError: isErrorMemberKpis,
    dataUpdatedAt: memberKpisUpdatedAt,
  } = useQuery(
    [
      "members-kyc-kpis",
      effectiveEmployerId,
      underReviewChecked,
      effectiveStoreId,
      dayPeriod,
    ],
    () =>
      fetchMemberKpis(
        effectiveEmployerId,
        underReviewChecked,
        effectiveStoreId,
        dayPeriod
      ),
    {
      staleTime: 60 * 1000,
      enabled: canViewSignupAndKpiAnalytics,
      refetchInterval: refreshInterval,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const {
    data: kycManualSubStatusDistributionData,
    isFetching: isFetchingKycManualSubStatusDistribution,
    isError: isErrorKycManualSubStatusDistribution,
    dataUpdatedAt: subStatusUpdatedAt,
  } = useQuery(
    [
      "members-kyc-manual-substatus-distribution",
      effectiveEmployerId,
      underReviewChecked,
      selectedOrderStatus,
      selectedOrderPeriod,
      dayPeriod,
      effectiveStoreId,
    ],
    () =>
      fetchKycManualSubStatusDistribution(
        effectiveEmployerId,
        underReviewChecked,
        effectiveStoreId,
        creationPeriod
      ),
    {
      staleTime: 60 * 1000,
      enabled:
        canViewOrderStatusAnalytics && selectedOrderStatus === "KYC_MANUAL",
      refetchInterval: refreshInterval,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  // Store chips stay visible while a store is selected so the filter can be
  // toggled off from the same chip.
  const isStoreDistributionVisible = canViewOrderStatusAnalytics;
  const {
    data: storeDistributionData,
    isFetching: isFetchingStoreDistribution,
    isError: isErrorStoreDistribution,
    dataUpdatedAt: storeDistributionUpdatedAt,
  } = useQuery(
    [
      "members-store-distribution",
      effectiveEmployerId,
      underReviewChecked,
      dayPeriod,
    ],
    () =>
      fetchMemberStoreDistribution(
        effectiveEmployerId,
        underReviewChecked,
        dayPeriod
      ),
    {
      staleTime: 60 * 1000,
      enabled: isStoreDistributionVisible,
      refetchInterval: refreshInterval,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const refreshing =
    isFetching ||
    isFetchingDataCount ||
    (canViewOrderStatusAnalytics &&
      (isFetchingOrderStatusDistribution ||
        (isHourlySignupsVisible && isFetchingHourlySignups) ||
        (canViewSignupAndKpiAnalytics && isFetchingMemberKpis) ||
        (isStoreDistributionVisible && isFetchingStoreDistribution) ||
        (isTotalLoadVisible &&
          isFetchingNetLoad) ||
        (selectedOrderStatus === "KYC_MANUAL" &&
          isFetchingKycManualSubStatusDistribution)));
  const refreshError =
    isError ||
    isErrorDataCount ||
    (canViewOrderStatusAnalytics &&
      (isErrorOrderStatusDistribution ||
        (isHourlySignupsVisible && isErrorHourlySignups) ||
        (canViewSignupAndKpiAnalytics && isErrorMemberKpis) ||
        (isStoreDistributionVisible && isErrorStoreDistribution) ||
        (isTotalLoadVisible && isErrorNetLoad) ||
        (selectedOrderStatus === "KYC_MANUAL" &&
          isErrorKycManualSubStatusDistribution)));
  const updatedAt = Math.min(
    dataUpdatedAt || 0,
    countUpdatedAt || 0,
    ...(canViewOrderStatusAnalytics
      ? [
          distributionUpdatedAt || 0,
          ...(canViewSignupAndKpiAnalytics ? [memberKpisUpdatedAt || 0] : []),
          ...(isHourlySignupsVisible ? [hourlySignupsUpdatedAt || 0] : []),
          ...(isStoreDistributionVisible
            ? [storeDistributionUpdatedAt || 0]
            : []),
          ...(isTotalLoadVisible
            ? [netLoadUpdatedAt || 0]
            : []),
          ...(selectedOrderStatus === "KYC_MANUAL"
            ? [subStatusUpdatedAt || 0]
            : []),
        ]
      : [])
  );

  const loadPeriodTitle = {
    last7Days: "Last 7 days",
    thisMonth: "This month",
    lastMonth: "Last month",
    today: "Today",
    yesterday: "Yesterday",
    thisWeek: "This week",
    allTime: "All time",
  }[loadPeriod] || loadPeriod;

  // Keep the selected store available at zero so its filter can be cleared.
  const renderStoreChipStrip = () => {
    const isLoadMode = analyticsView === "totalLoad";
    const noStoreLabel = Labels.getLabel(
      "members.distribution.noStore",
      "No store"
    );
    let entries;
    if (isLoadMode) {
      const stores = netLoadData?.data?.stores;
      entries = (Array.isArray(stores) ? stores : [])
        .filter(
          (entry) =>
            Number(entry.netLoad || 0) !== 0 ||
            Number(entry.totalLoad || 0) !== 0 ||
            Number(entry.loadCount || 0) > 0
        )
        .map((entry) => ({ ...entry, value: Number(entry.netLoad || 0) }));
    } else {
      const byPeriod = storeDistributionData?.data || {};
      const periodTotals = Object.fromEntries(
        DISTRIBUTION_PERIODS.map(([period]) => [
          period,
          (byPeriod[period] || []).reduce(
            (sum, entry) => sum + Number(entry.count || 0),
            0
          ),
        ])
      );
      entries = (byPeriod[selectedOrderPeriod] || [])
        .filter((entry) => Number(entry.count || 0) > 0)
        .map((entry) => ({
          storeId: entry.storeId,
          value: Number(entry.count || 0),
          periods: DISTRIBUTION_PERIODS.map(([period, title]) => {
            const count = Number(
              (byPeriod[period] || []).find(
                (item) => String(item.storeId) === String(entry.storeId)
              )?.count || 0
            );
            return {
              period,
              title: period === "today" ? dayTitle : title,
              count,
              pct: periodTotals[period]
                ? Math.round((count / periodTotals[period]) * 100)
                : 0,
            };
          }),
        }));
    }
    const storeCount = entries.filter((entry) => entry.storeId).length;
    const visibleEntries = effectiveStoreId
      ? [
          entries.find(
            (entry) => String(entry.storeId) === String(effectiveStoreId)
          ) || {
            storeId: effectiveStoreId,
            value: 0,
            netLoad: 0,
            totalLoad: 0,
            loadCount: 0,
            periods: DISTRIBUTION_PERIODS.map(([period, title]) => ({
              period,
              title: period === "today" ? dayTitle : title,
              count: 0,
              pct: 0,
            })),
          },
        ]
      : entries;
    if (isLoadMode && !netLoadData) {
      return isErrorNetLoad ? (
        <Typography color="error">Unable to load store amounts.</Typography>
      ) : <LinearProgress />;
    }
    return (
      <Box
        role="group"
        aria-label={isLoadMode ? "Net load by store" : "Members by store"}
        data-testid="member-store-chips"
        data-mode={isLoadMode ? "load" : "members"}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          minWidth: 0,
          mt: 0.75,
          pt: 0.75,
          borderTop: "1px dashed",
          borderColor: "divider",
        }}
      >
        <Tooltip
          arrow
          title={
            effectiveStoreId
              ? "Filtered to one store"
              : isLoadMode
                ? `Sorted by net load · ${loadPeriodTitle}`
                : `Members per store, ${selectedPeriodTitle}`
          }
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              fontSize: "0.68rem",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              whiteSpace: "nowrap",
            }}
          >
            {effectiveStoreId
              ? "By store · filtered"
              : `By store · ${storeCount}`}
            {!isLoadMode && ` · ${selectedPeriodTitle}`}
            {isLoadMode && ` · ${loadPeriodTitle}`}
          </Typography>
        </Tooltip>
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 0.5,
            overflowX: "auto",
            minWidth: 0,
            py: 0.25,
            "& > *": { flexShrink: 0 },
          }}
        >
          {visibleEntries.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              {isLoadMode
                ? "No successful load or unload activity in this period."
                : Labels.getLabel(
                    "members.distribution.empty",
                    "No members created in this period."
                  )}
            </Typography>
          )}
          {visibleEntries.map((entry) => {
            const hasStore = Boolean(entry.storeId);
            const isSelected =
              hasStore &&
              String(entry.storeId) === String(effectiveStoreId || "");
            const label = hasStore ? `#${entry.storeId}` : noStoreLabel;
            const clickHint = hasStore && (
              <div>
                {isSelected ? "Click to clear filter" : "Click to filter"}
              </div>
            );
            let tooltip;
            let badge;
            let badgeColor;
            if (isLoadMode) {
              const net = entry.value;
              const total = Number(entry.totalLoad || 0);
              const loads = Number(entry.loadCount || 0);
              badge = formatCurrency(net);
              badgeColor = !hasStore
                ? "grey.500"
                : net < 0
                  ? "error.main"
                  : NET_LOAD_COLOR;
              tooltip = (
                <Box sx={{ fontSize: "0.72rem" }}>
                  <strong>{label}</strong>
                  <div>Net: {formatCurrency(net)}</div>
                  <div>Total load: {formatCurrency(total)}</div>
                  <div>Deposits: {loads}</div>
                  <div>
                    Avg deposit (loads):{" "}
                    {loads ? formatCurrency(total / loads) : "—"}
                  </div>
                  <div>
                    Avg net deposit: {loads ? formatCurrency(net / loads) : "—"}
                  </div>
                  {clickHint}
                </Box>
              );
            } else {
              badge = entry.value;
              badgeColor = hasStore ? "primary.main" : "grey.500";
              tooltip = (
                <Box sx={{ fontSize: "0.72rem" }}>
                  <strong>{label}</strong>
                  {entry.periods.map((item) => (
                    <div key={item.period}>
                      {item.title}: {item.count} ({item.pct}%)
                    </div>
                  ))}
                  {clickHint}
                </Box>
              );
            }
            return (
              <Tooltip
                key={`store-${entry.storeId || "none"}`}
                title={tooltip}
                arrow
              >
                <Chip
                  size="small"
                  data-testid={`member-store-chip-${entry.storeId || "none"}`}
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <span>{label}</span>
                      <Box
                        component="span"
                        sx={{
                          px: 0.6,
                          borderRadius: 8,
                          fontWeight: 800,
                          fontSize: "0.68rem",
                          bgcolor: isSelected ? "common.white" : badgeColor,
                          color: isSelected ? "primary.dark" : "common.white",
                        }}
                      >
                        {badge}
                      </Box>
                    </Box>
                  }
                  aria-pressed={hasStore ? isSelected : undefined}
                  onClick={
                    hasStore
                      ? () => selectStore(isSelected ? "" : entry.storeId)
                      : undefined
                  }
                  onDelete={isSelected ? () => selectStore("") : undefined}
                  variant={isSelected ? "filled" : "outlined"}
                  color={isSelected ? "primary" : "default"}
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: isSelected ? 700 : 600,
                    ...(isSelected
                      ? { "& .MuiChip-deleteIcon": { fontSize: 14, mr: 0.4 } }
                      : {
                          borderColor: hasStore ? "primary.light" : "divider",
                          color: hasStore ? "primary.dark" : "text.secondary",
                          bgcolor: hasStore
                            ? "rgba(25, 118, 210, 0.06)"
                            : "grey.50",
                          "&:hover": hasStore
                            ? { bgcolor: "rgba(25, 118, 210, 0.14)" }
                            : undefined,
                        }),
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    );
  };

  const orderStatusDistribution = React.useMemo(() => {
    return groupStatisticStatuses(
      orderStatusDistributionData?.data?.allTime || []
    ).map((entry) => ({
      rawStep: entry.status || "UNKNOWN",
      shortStep: entry.status || "UNKNOWN",
      count: entry.count || 0,
      color: getStatusColor(entry.status),
    }));
  }, [orderStatusDistributionData]);

  const totalOrderStatusCount = React.useMemo(
    () =>
      orderStatusDistribution.reduce(
        (sum, entry) => sum + Number(entry.count || 0),
        0
      ),
    [orderStatusDistribution]
  );

  const selectedStepCount =
    selectedOrderStatus === null
      ? null
      : groupStatisticStatuses(
          orderStatusDistributionData?.data?.[selectedOrderPeriod] || []
        ).find(
          (entry) => String(entry.status) === String(selectedOrderStatus)
        )?.count || 0;
  const selectedPeriodTitle =
    selectedOrderPeriod === "today"
      ? dayTitle
      : Labels.getLabel(
          `members.distribution.${selectedOrderPeriod}`,
          DISTRIBUTION_PERIODS.find(
            ([period]) => period === selectedOrderPeriod
          )[1]
        );

  const selectOrderStatus = (status, period) => {
    const next =
      selectedOrderStatus === status && selectedOrderPeriod === period
        ? null
        : status;
    setSelectedOrderStatus(next);
    setSelectedOrderPeriod(next ? period : "allTime");
    if (next !== "KYC_MANUAL") {
      setSelectedOrderSubStatus(null);
    }
  };

  const selectOrderPeriod = (period) => {
    setSelectedOrderPeriod(period);
    setSelectedOrderStatus(null);
    setSelectedOrderSubStatus(null);
  };

  const selectDay = (day) => {
    setDayPeriod(day);
    selectOrderPeriod("today");
    setDayMenuAnchor(null);
    setDayDialogOpen(false);
    setDayError(false);
  };

  const selectAnalyticsPeriod = (period) => {
    if (["allTime", "thisMonth", "thisWeek"].includes(period)) {
      selectOrderPeriod(period);
      setDayPeriod("today");
      setDayMenuAnchor(null);
    } else {
      selectDay(period);
    }
  };

  const openMonthSelection = () => {
    setDayMenuAnchor(null);
    setCustomLoadMonth(/^\d{4}-\d{2}$/.test(loadPeriod) ? loadPeriod : "");
    setLoadMonthError(false);
    setLoadMonthDialogOpen(true);
  };

  const renderPeriodControls = () => (
    <Tooltip title={Labels.getLabel("members.distribution.chooseDay", "Select period")}>
      <IconButton
        size="small"
        color="primary"
        aria-label={Labels.getLabel("members.distribution.chooseDay", "Select period")}
        onClick={(event) => setDayMenuAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(dayMenuAnchor)}
        aria-controls={dayMenuAnchor ? "member-day-menu" : undefined}
        sx={{
          flexShrink: 0,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          bgcolor: creationPeriod === "allTime" ? "transparent" : "action.selected",
          "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" },
        }}
      >
        <CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );

  const applyCustomDay = () => {
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(customDay) ||
      Number.isNaN(Date.parse(`${customDay}T00:00:00Z`)) ||
      new Date(`${customDay}T00:00:00Z`).toISOString().slice(0, 10) !== customDay ||
      Number(customDay.slice(0, 4)) < 1 ||
      Number(customDay.slice(0, 4)) >= 9999 ||
      customDay > maxDay
    ) {
      setDayError(true);
      return;
    }
    selectDay(customDay);
  };

  const kycManualSubStatusDistribution = React.useMemo(() => {
    const allowed = new Set([
      "RESTRICTED",
      "HARD_FAIL",
      "FAILED",
      "PENDING_SCAN_REVIEW",
      "DOCS_UPLOADED",
    ]);
    return (kycManualSubStatusDistributionData?.data || [])
      .map((entry) => ({
        status: entry.status || "UNKNOWN",
        count: Number(entry.count || 0),
      }))
      .filter((entry) => allowed.has(entry.status));
  }, [kycManualSubStatusDistributionData]);

  // selected order status drives backend query filter for members table.
  useEffect(() => {
    if (setOrderStatusFilterFunction) {
      setOrderStatusFilterFunction(selectedOrderStatus, creationPeriod);
    }
    if (selectedOrderStatus !== "KYC_MANUAL") {
      setSelectedOrderSubStatus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderStatus, creationPeriod]);

  useEffect(() => {
    if (setOrderSubStatusFilterFunction) {
      setOrderSubStatusFilterFunction(selectedOrderSubStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderSubStatus]);

  const handleCheckboxChange = (event) => {
    const checked = event.target.checked;
    setUnderReviewChecked(checked);
    setSelectedOrderStatus(null);
    setSelectedOrderPeriod("allTime");
    setSelectedOrderSubStatus(null);
    setUnderReviewOnlyFunction(checked);
  };

  const onFilterChange = (filter, params) => {
    setFilterModel(filter);
    const filterString = filter.quickFilterValues.join(" ");
    if (filterString === undefined) {
      setFilterStringFunction("");
    } else {
      setFilterStringFunction(filterString);
    }
    return filter;
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // Add this near your other state declarations
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    creationDate: false,
    userStatus: false,
    // Add any other default column visibility settings here
  });

  // Then add this handler
  const handleColumnVisibilityModelChange = (newModel) => {
    setColumnVisibilityModel(newModel);
  };

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const [openNewDlg, setOpenNewDlg] = useState(false);
  const [openEditMemberDlg, setOpenEditMemberDlg] = useState(false);
  const [openEditEmailDlg, setOpenEditEmailDlg] = useState(false);
  const [openSecureInfoDlg, setOpenSecureInfoDlg] = useState(false);
  const [openCardActionsDlg, setOpenCardActionsDlg] = useState(false);
  const [openTransactionsDlg, setOpenTransactionsDlg] = useState(false);
  const [openStatementsDlg, setOpenStatementsDlg] = useState(false);
  const [memberInfo, setMemberInfo] = useState({});

  const onViewButtonClick = (e, params) => {
    setMemberInfo(params);
    setOpenNewDlg(true);
  };

  const onEditMemberClick = (e, params) => {
    setMemberInfo(params);
    setOpenEditMemberDlg(true);
  };

  const onEditEmailClick = (e, params) => {
    setMemberInfo(params);
    setOpenEditEmailDlg(true);
  };

  const onSecureInfoClick = (e, params) => {
    setMemberInfo(params);
    setOpenSecureInfoDlg(true);
  };

  const onCardActionsClick = (e, params) => {
    setMemberInfo(params);
    setOpenCardActionsDlg(true);
  };

  const onViewTransactionsClick = (e, params) => {
    setMemberInfo(params);
    setOpenTransactionsDlg(true);
  };

  const onViewStatementsClick = (e, params) => {
    setMemberInfo(params);
    setOpenStatementsDlg(true);
  };

  const copyIdentifier = (value) => {
    if (value !== null && value !== undefined && value !== "") {
      navigator.clipboard.writeText(String(value));
    }
  };

  const resendVirtualCardMutation = useMutation(
    (idCustomer) =>
      HttpService.getAxiosClient().post(
        window.API_URL + `/member/${idCustomer}/resend`
      ),
    {
      onSuccess: () => {
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "members.resendVirtualCardSuccess",
            "Virtual card email resent successfully"
          ),
        });
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({ open: true, type: "error", message: msg });
      },
    }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataColumns = [
    {
      field: "id",
      headerName: Labels.getLabel("members.column.id", "Rellevate Platform ID"),
      width: 260,
      renderCell: (params) => {
        const processorId =
          params.row.processorId !== null &&
          params.row.processorId !== "" &&
          params.row.processorId !== undefined
            ? params.row.processorId
            : params.row.cardOrderConfirmationNumber;
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "36px 20px minmax(0, 1fr)",
                alignItems: "center",
                fontSize: 10,
                mb: 0.5,
              }}
            >
              <span
                style={{
                  textAlign: "right",
                  fontWeight: "bold",
                }}
              >
                RID:
              </span>
              <Tooltip title="Copy Rellevate Customer ID">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    copyIdentifier(params.row.id);
                  }}
                  sx={{ p: 0.1 }}
                >
                  <ContentCopyIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {params.row.id}
              </span>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "36px 20px minmax(0, 1fr)",
                alignItems: "center",
                fontSize: 10,
              }}
            >
              <span
                style={{
                  textAlign: "right",
                  fontWeight: "bold",
                }}
              >
                PID:
              </span>
              <Tooltip title="Copy Processor ID">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    copyIdentifier(processorId);
                  }}
                  sx={{ p: 0.1 }}
                >
                  <ContentCopyIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {processorId}
              </span>
            </Box>
          </div>
        );
      },
    },
    {
      field: "memberId",
      headerName: Labels.getLabel("members.column.memberId", "Member Id"),
      width: 150,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "6rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ minWidth: "8rem", fontSize: 14, mb: 0 }}
              gutterBottom
            >
              {params.row.memberId}
            </Typography>
            <Typography
              sx={{
                minWidth: "8rem",
                fontSize: 8,
                mb: 0,
                fontWeight: "bold",
                color: "gray",
              }}
              gutterBottom
            >
              {params.row.companyName}
            </Typography>
            {params.row.storeId ? (
              <Typography
                data-testid={`member-store-${params.row.id}`}
                sx={{
                  minWidth: "8rem",
                  fontSize: 10,
                  mb: 0,
                  fontWeight: "bold",
                  color: "#1565c0",
                }}
              >
                {Labels.getLabel("members.storeLabel", "Store #")}
                {params.row.storeId}
              </Typography>
            ) : null}
          </div>
        );
      },
    },
    {
      field: "firstName",
      headerName: Labels.getLabel("members.column.name", "Name"),
      width: 180,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 14, mb: 0 }} gutterBottom>
            {params.row.firstName} {params.row.lastName}
          </Typography>
        );
      },
    },
    {
      field: "email",
      headerName: Labels.getLabel("members.column.contactInfo", "Contact Info"),
      width: 280,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "20rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "left",
              alignItems: "left",
              textAlign: "left",
            }}
          >
            {params.row.email && params.row.email !== "" ? (
              <Typography
                sx={{ minWidth: "20rem", fontSize: 14, mb: 0 }}
                gutterBottom
              >
                <span style={{ fontWeight: "bold" }}>
                  {Labels.getLabel("members.emailLabel", "Email:")}
                </span>{" "}
                {params.row.email.includes("_noemail_")
                  ? Labels.getLabel("members.noEmail", "No Email")
                  : params.row.email}
              </Typography>
            ) : (
              ""
            )}
            {params.row.mobileNumber && params.row.mobileNumber !== "" ? (
              <Typography
                sx={{ minWidth: "20rem", fontSize: 10, mb: 0 }}
                gutterBottom
              >
                <span style={{ fontWeight: "bold" }}>
                  {Labels.getLabel("members.phoneLabel", "Phone:")}
                </span>{" "}
                {params.row.cardType === "INCENTIVE"
                  ? Labels.getLabel("members.noPhone", "No Phone")
                  : params.row.mobileNumber}
              </Typography>
            ) : (
              ""
            )}
          </div>
        );
      },
    },

    {
      field: "creationDate",
      headerName: Labels.getLabel("members.column.createdAt", "Created At"),
      width: 130,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.creationDate}
          </Typography>
        );
      },
    },

    {
      field: "cardType",
      headerName: Labels.getLabel("members.column.cardType", "Card Type"),
      width: 140,
      renderCell: (params) => {
        const identificationType =
          params.row.mainIdentificationType ?? params.row.mainDocumentType;
        const limitType = String(params.row.limitType || "").toUpperCase();
        const limitStatus = {
          NOT_LIMITED: { label: "NOT LIMITED", color: "#2e7d32" },
          LIMITED: { label: "LIMITED", color: "#f9a825" },
          NO_FUNCTIONALITY: { label: "NO FUNCTIONALITY", color: "#d32f2f" },
          NO_FUNCTKIONALITY: { label: "NO FUNCTIONALITY", color: "#d32f2f" },
        }[limitType];

        return (
          <div
            style={{
              minWidth: "8rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.cardType}
            </Typography>
            <Typography
              component="div"
              sx={{ fontSize: 8, mb: 0, fontWeight: "bold", color: "gray" }}
              gutterBottom
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {params.row.processorProvider}
                <Tooltip title={limitStatus?.label || ""} placement="top">
                  <Chip
                    label={
                      String(identificationType) === "1" ? "SSN" : "NO SSN"
                    }
                    size="small"
                    aria-label={
                      String(identificationType) === "1" ? "SSN" : "NO SSN"
                    }
                    sx={{
                      width: 42,
                      height: 16,
                      fontSize: 8,
                      fontWeight: "bold",
                      color: limitStatus ? "#fff" : "transparent",
                      backgroundColor: limitStatus?.color || "transparent",
                      "& .MuiChip-label": { px: 0.6 },
                    }}
                  />
                </Tooltip>
              </Box>
            </Typography>
          </div>
        );
      },
    },
    {
      field: "cardRequestStatus",
      headerName: Labels.getLabel("members.column.orderStatus", "Order Status"),
      width: 260,
      renderCell: ({ row }) => {
        const elapsedDetail = formatMemberElapsedTime(
          row.cardLimitReasonElapsedMillis
        );
        const elapsed = formatMemberElapsedTimeCompact(
          row.cardLimitReasonElapsedMillis
        );
        const counting = row.cardLimitReasonCounting;
        const invalidTiming = row.cardLimitReasonElapsedMillis < 0;
        const timingColor =
          elapsed === null
            ? "default"
            : invalidTiming
              ? "error"
              : counting
                ? "warning"
                : "success";
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 0.5,
              width: "100%",
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0, width: 150, flex: "0 0 150px" }}>
              <Typography noWrap sx={{ fontSize: 12, fontWeight: 700 }}>
                {row.cardRequestStatus}
              </Typography>
              <Typography noWrap sx={{ fontSize: 10 }} color="text.secondary">
                {row.enrollmentStatus}
              </Typography>
            </Box>
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" display="block">
                    Elapsed:{" "}
                    {elapsedDetail ||
                      Labels.getLabel("members.notAvailable", "N/A")}
                  </Typography>
                </Box>
              }
            >
              <Chip
                size="small"
                variant="outlined"
                color={timingColor}
                icon={
                  counting || elapsed === null || invalidTiming ? (
                    <AccessTimeIcon />
                  ) : (
                    <CheckCircleOutlineIcon />
                  )
                }
                label={
                  elapsed ?? Labels.getLabel("members.notAvailable", "N/A")
                }
                sx={{
                  height: 24,
                  width: 68,
                  fontSize: 11,
                  fontWeight: /d$/.test(elapsed || "")
                    ? 800
                    : /h$/.test(elapsed || "")
                      ? 700
                      : /m$/.test(elapsed || "")
                        ? 600
                        : 400,
                  borderRadius: 1.5,
                  "& .MuiChip-icon": {
                    fontSize: 15,
                    ...(counting && {
                      animation: "memberElapsedPulse 2s ease-in-out infinite",
                    }),
                  },
                  "& .MuiChip-label": { px: 0.75 },
                  "@keyframes memberElapsedPulse": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.7, transform: "scale(0.92)" },
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    "& .MuiChip-icon": { animation: "none" },
                  },
                }}
              />
            </Tooltip>
          </Box>
        );
      },
    },

    {
      field: "userStatus",
      headerName: Labels.getLabel("members.column.userStatus", "User status"),
      width: 100,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.userStatus ? params.row.userStatus : "ACTIVE"}
            </Typography>
            <Typography
              sx={{ fontSize: 8, mb: 0, fontWeight: "bold", color: "gray" }}
              gutterBottom
            >
              {params.row.oldStatus}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "details",
      headerName: Labels.getLabel("members.column.actions", "Actions"),
      width: 300,
      renderCell: (params) => {
        const cardActive =
          (params.row.cardRequestStatus === "COMPLETED" ||
            params.row.cardRequestStatus === "REQUESTED" ||
            params.row.cardRequestStatus === "KYC_MORE_INFO" ||
            params.row.cardRequestStatus === "KYC_MANUAL") &&
          params.row.enrollmentStatus !== "Under Review";
        const isVirtual =
          !params.row.email?.includes("_noemail_") &&
          (params.row.cardType === "VIRTUAL" ||
            params.row.issuanceType === "VIRTUAL" ||
            params.row.issuanceType === "VIRTUALONETIME");
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            <Tooltip
              title={Labels.getLabel("members.viewMemberInfo", "View PAD info")}
            >
              <IconButton
                color="primary"
                size="small"
                onClick={(e) => onViewButtonClick(e, params.row)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <RenderOnRole roles={["member-fix"]}>
              <Tooltip
                title={Labels.getLabel(
                  "members.changeLocation",
                  "Edit Company/MemberId"
                )}
              >
                <IconButton
                  color="primary"
                  size="small"
                  onClick={(e) => onEditMemberClick(e, params.row)}
                >
                  <MoveDownIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </RenderOnRole>
            {params.row.email && params.row.email !== "" ? (
              <RenderOnRole roles={["member-change-email"]}>
                <Tooltip
                  title={Labels.getLabel(
                    "members.changeEmail",
                    "Edit Username/email"
                  )}
                >
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={(e) => onEditEmailClick(e, params.row)}
                  >
                    <AlternateEmailIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </RenderOnRole>
            ) : null}
            <RenderOnRole roles={["compliance-mgr","compliance-viewer"]}>
              <Tooltip
                title={Labels.getLabel(
                  "members.secureInfo",
                  "View Secure Information"
                )}
              >
                <IconButton
                  color="primary"
                  size="small"
                  onClick={(e) => onSecureInfoClick(e, params.row)}
                >
                  <LockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </RenderOnRole>
            {cardActive ? (
              <RenderOnRole roles={["pc-lock-unlock-cards"]}>
                <Tooltip
                  title={Labels.getLabel("members.cardActions", "Card Actions")}
                >
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={(e) => onCardActionsClick(e, params.row)}
                  >
                    <CreditCardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </RenderOnRole>
            ) : null}
            {cardActive ? (
              <RenderOnRole roles={["pc-view-trx-cards"]}>
                <Tooltip
                  title={Labels.getLabel(
                    "members.viewTransactions",
                    "View Transactions"
                  )}
                >
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={(e) => onViewTransactionsClick(e, params.row)}
                  >
                    <ReadMoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </RenderOnRole>
            ) : null}
            {isVirtual ? (
              <RenderOnRole roles={["pc-lock-unlock-cards"]}>
                <Tooltip
                  title={Labels.getLabel(
                    "members.resendVirtualCardEmail",
                    "Resend virtual card email"
                  )}
                >
                  <span>
                    <IconButton
                      color="primary"
                      size="small"
                      disabled={resendVirtualCardMutation.isLoading}
                      onClick={() =>
                        resendVirtualCardMutation.mutate(params.row.idCustomer)
                      }
                    >
                      <ForwardToInboxIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </RenderOnRole>
            ) : null}
            <RenderOnRole roles={["read-statements"]}>
              <Tooltip
                title={Labels.getLabel(
                  "members.viewStatements",
                  "View Statements"
                )}
              >
                <IconButton
                  color="primary"
                  size="small"
                  onClick={(e) => onViewStatementsClick(e, params.row)}
                >
                  <DescriptionIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </RenderOnRole>
          </Box>
        );
      },
    },
  ];

  const columns = React.useMemo(
    () => [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        headerName: Labels.getLabel(
          "members.column.changeLocation",
          "Change Location"
        ),
        width: 100,
      },
      ...dataColumns,
    ],
    [dataColumns]
  );

  const handleRowSelection = (rows) => {
    setSelection(rows);
  };

  useEffect(() => {}, [select]);
  return (
    <Fragment>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <div style={{ margin: "0" }}>
          <h3 style={{ margin: "20px" }}>{title}</h3>
          <Box sx={{ mx: 2.5, mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={Boolean(automaticRefresh)}
                  onChange={(event) => setAutomaticRefreshFunction(event.target.checked)} />}
                label={Labels.getLabel("members.automaticRefresh", "Automatic refresh")}
              />
              <Button size="small" variant="outlined" disabled={refreshing} onClick={refreshMembers}>
                {Labels.getLabel("members.refreshNow", "Refresh now")}
              </Button>
            </Box>
            <Box role="status" aria-live="polite">
            <Box sx={{ display: "grid" }} data-testid="member-refresh-message">
              <Typography
                variant="body2"
                aria-hidden={refreshing}
                sx={{
                  gridArea: "1 / 1",
                  visibility: refreshing ? "hidden" : "visible",
                  whiteSpace: "nowrap",
                  fontSize: "0.875rem",
                  color: "text.secondary",
                }}
              >
                {automaticRefresh ? Labels.getLabel(
                  "members.autoRefresh",
                  "Auto-refresh every 10 seconds"
                ) : Labels.getLabel("members.manualRefresh", "Automatic refresh is off")}
              </Typography>
              <Typography
                variant="body2"
                aria-hidden={!refreshing}
                sx={{
                  gridArea: "1 / 1",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  visibility: refreshing ? "visible" : "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                <CircularProgress size="1rem" color="inherit" aria-hidden />
                {Labels.getLabel(
                  "refreshingIndicator.refreshing",
                  "Refreshing..."
                )}
              </Typography>
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", minHeight: "1.66em" }}
            >
              {updatedAt > 0 && (
                <>
                  {Labels.getLabel("members.lastUpdated", "Last updated:")}{" "}
                  {new Date(updatedAt).toLocaleTimeString()}
                </>
              )}
            </Typography>
          </Box>
        </div>
        <div
          style={{
            padding: "0px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Autocomplete
            sx={{ minWidth: 300 }}
            options={employersData?.data || []}
            getOptionLabel={(option) =>
              `${option.name || ""} [${option.idEmployer || ""}]`
            }
            value={selectedEmployer}
            onChange={handleEmployerChange}
            loading={isLoadingEmployers}
            clearOnBlur={false}
            disableClearable={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Labels.getLabel(
                  "members.filterByEmployer",
                  "Filter by Employer"
                )}
                size="small"
                variant="outlined"
                sx={{ minWidth: "600px" }}
              />
            )}
            isOptionEqualToValue={(option, value) => {
              if (!value) return false;
              return option.idEmployer === value.idEmployer;
            }}
          />
          <TextField
            select
            size="small"
            label={Labels.getLabel("members.filterByStore", "Store")}
            value={selectedStore}
            onChange={handleStoreChange}
            disabled={isLoadingStores}
            sx={{ minWidth: 180 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "member-store-filter" }}
          >
            <MenuItem value="">
              {Labels.getLabel("members.allStores", "All stores")}
            </MenuItem>
            {storeOptions.map((store) => (
              <MenuItem key={store} value={store}>
                {store}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={underReviewChecked}
                onChange={handleCheckboxChange}
                color="primary"
              />
            }
            label={Labels.getLabel(
              "members.underReviewOnly",
              "Under Review only"
            )}
          />
        </div>
      </div>

      {canViewOrderStatusAnalytics && (
        <Accordion
          expanded={analyticsExpanded}
          onChange={(_event, expanded) => setAnalyticsExpanded(expanded)}
          sx={{ mb: 0.75, borderRadius: 1.25, overflow: "hidden" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: "grey.50",
              minHeight: 34,
              "& .MuiAccordionSummary-content": { my: 0.25, minWidth: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                width: "100%",
                minWidth: 0,
              }}
            >
              <FilterListIcon fontSize="small" color="primary" />
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                Order Status Distribution
              </Typography>
              <Chip
                size="small"
                label={`${totalOrderStatusCount} members`}
                variant="outlined"
                sx={{ height: 18, fontSize: "0.7rem" }}
              />
              {(selectedOrderStatus !== null || selectedOrderPeriod !== "allTime") && (
                <Chip
                  size="small"
                  label={selectedOrderStatus !== null
                    ? `Selected: ${selectedOrderStatus} (${selectedStepCount}) - ${selectedPeriodTitle}`
                    : `Selected period: ${selectedPeriodTitle}`}
                  onDelete={() => {
                    setSelectedOrderStatus(null);
                    setSelectedOrderPeriod("allTime");
                    setSelectedOrderSubStatus(null);
                  }}
                  color="primary"
                  sx={{ height: 18, fontSize: "0.7rem" }}
                />
              )}
              {selectedOrderStatus === "KYC_MANUAL" &&
                selectedOrderSubStatus && (
                  <Chip
                    size="small"
                    label={`Subfilter: ${selectedOrderSubStatus}`}
                    onDelete={() => setSelectedOrderSubStatus(null)}
                    color="warning"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.7rem" }}
                  />
                )}
              {/* Tabs live in the header; keep their clicks from toggling the accordion. */}
              <Box
                onClick={(event) => event.stopPropagation()}
                onFocus={(event) => event.stopPropagation()}
                sx={{ ml: 1, minWidth: 0, maxWidth: "100%" }}
              >
                <Tabs
                  value={analyticsView}
                  onChange={(_event, value) => {
                    setAnalyticsView(value);
                    setAnalyticsExpanded(true);
                  }}
                  aria-label="Choose member analytics view"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 28,
                    "& .MuiTabs-indicator": { height: 2 },
                    "& .MuiTab-root": {
                      minHeight: 28,
                      minWidth: 0,
                      py: 0.25,
                      px: 1.25,
                      fontSize: "0.72rem",
                      lineHeight: 1.2,
                    },
                  }}
                >
                  <Tab value="distribution" label="Distribution" />
                  {canViewSignupAndKpiAnalytics && <Tab value="kpis" label="KPIs" />}
                  {canViewManagementAnalytics && <Tab value="last7Days" label="Last 7 days (SIGNUPS)" />}
                  {canViewManagementAnalytics && <Tab value="totalLoad" label="Load Volume" />}
                  {canViewManagementAnalytics && <Tab value="hourlySignups" label="Hourly Signups" />}
                </Tabs>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0.75, pb: 1, bgcolor: "#fafafa" }}>
            <Box sx={{ minWidth: 0 }}>
              <Box
                data-testid="member-analytics-view-content"
                sx={{
                  minWidth: 0,
                  height: 320,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {isHourlySignupsVisible && (
                  <Box component="section" aria-label={`Hourly signups, ${loadPeriodTitle.toLowerCase()}`}
                    sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1.5, bgcolor: "background.paper" }}>
                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Signups by hour · {loadPeriodTitle}
                        {effectiveStoreId ? ` · store ${effectiveStoreId}` : ""}
                      </Typography>
                      <Tooltip arrow open={signupInfoOpen}
                        onOpen={() => setSignupInfoOpen(true)} onClose={() => setSignupInfoOpen(false)}
                        title={`Blue bars: maximum signups at that hour on any single matching day. Orange line: average signups at that hour per day. Both use the same scale. Eastern Time (EST/EDT) and Pacific Time (PST/PDT) include historical daylight-saving changes. The weekday filter applies only to this view and uses the selected display time zone for both weekday and hour. Weekday averages divide by matching regional dates within the shared period, including partial boundary dates; excluding quiet days uses only matching dates with signups at any hour. Calendar days include today but exclude future days; All time starts on the first matching signup. Including days with no signups changes averages only, not maxima. All days retains the shared source-calendar denominator. Period boundaries use the shared source time zone: ${hourlySignupsData?.data?.sourceTimeZone || "backend system time zone"}.`}>
                        <IconButton size="small" aria-label="About hourly signups"
                          onClick={() => setSignupInfoOpen((open) => !open)}>
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    <Box role="group" aria-label="Signup time zone" sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
                      {[
                        ["eastern", "EST/EDT"],
                        ["pacific", "PST/PDT"],
                      ].map(([zone, title]) => (
                        <Button key={zone} size="small" aria-pressed={signupTimeZone === zone}
                          variant={signupTimeZone === zone ? "contained" : "outlined"}
                          onClick={() => setSignupTimeZone(zone)} sx={{ textTransform: "none" }}>
                          {title}
                        </Button>
                      ))}
                    </Box>
                      {renderPeriodControls()}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Box role="group" aria-label="Signup day of week" sx={{ display: "flex", flexWrap: "wrap", gap: 0.25 }}>
                      {["All days", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((title, weekday) => (
                        <Button key={title} size="small" aria-pressed={signupWeekday === weekday}
                          variant={signupWeekday === weekday ? "contained" : "text"}
                          onClick={() => setSignupWeekday(weekday)}
                          sx={{ minWidth: 36, textTransform: "none" }}>{title}</Button>
                      ))}
                    </Box>
                    <FormControlLabel
                      control={<Checkbox checked={includeEmptySignupDays}
                        onChange={(event) => setIncludeEmptySignupDays(event.target.checked)} size="small" />}
                      label="Include days with no signups"
                    />
                    </Box>
                    {isLoadingHourlySignups ? <CircularProgress size={24} /> :
                      isErrorHourlySignups && !hourlySignupsData ?
                        <Typography color="error">Unable to load hourly signups.</Typography> :
                        (() => {
                          const weekdayData = signupWeekday === 0 ? null
                            : (signupTimeZone === "eastern"
                              ? hourlySignupsData?.data?.easternWeekdays
                              : hourlySignupsData?.data?.pacificWeekdays)?.find((day) => day.weekday === signupWeekday);
                          const selectedData = signupWeekday === 0 ? hourlySignupsData?.data : weekdayData;
                          const hours = (signupWeekday !== 0 ? weekdayData?.buckets : signupTimeZone === "eastern"
                            ? hourlySignupsData?.data?.buckets : hourlySignupsData?.data?.pacificBuckets) || [];
                          const zoneTitle = signupTimeZone === "eastern"
                            ? "Eastern Time (EST/EDT)" : "Pacific Time (PST/PDT)";
                          const averageDays = Number(includeEmptySignupDays
                            ? selectedData?.calendarDays : selectedData?.activeDays);
                          const average = (count) => averageDays > 0 ? Number(count) / averageDays : 0;
                          const total = hours.reduce((sum, bucket) => sum + Number(bucket.count), 0);
                          const peak = Math.max(0, ...hours.map((bucket) => Number(bucket.maxCount)));
                          return (
                            <>
                              <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 1 }}>
                              <Typography variant="body2" data-testid="hourly-signups-max">
                                Max at one hour: {peak.toLocaleString()} signups
                              </Typography>
                              <Typography variant="caption" data-testid="hourly-signups-average">
                                Average: {average(total).toLocaleString(undefined, { maximumFractionDigits: 2 })} signups/day
                                {" · "}{averageDays} {includeEmptySignupDays ? "calendar" : "active"} days
                                {signupWeekday !== 0 && ` · ${["", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays", "Sundays"][signupWeekday]} included`}
                              </Typography>
                              </Box>
                              {total === 0 && <Typography variant="caption">No signups in this period.</Typography>}
                              <Box role="group" aria-label={zoneTitle}
                                data-testid="hourly-signups-chart"
                                sx={{ overflowX: "auto", mt: 1 }}>
                                <Box sx={{ minWidth: 760 }}>
                                  <ResponsiveChartContainer height={220}
                                    xAxis={[{ id: "signupHour", scaleType: "band", data: hours.map((bucket) => bucket.hour),
                                      valueFormatter: (hour) => `${String(hour).padStart(2, "0")}:00`,
                                      tickLabelStyle: { fontSize: 10 } }]}
                                    yAxis={[{ id: "signupCount", min: 0 }]}
                                    series={[{ type: "bar", yAxisKey: "signupCount", data: hours.map((bucket) => Number(bucket.maxCount)),
                                      label: "Max on a single day", color: "#1976d2",
                                      valueFormatter: (count) => `${count.toLocaleString()} signups on one day` },
                                      { type: "line", yAxisKey: "signupCount", data: hours.map((bucket) => average(bucket.count)),
                                        label: "Avg signups/day", color: "#ed6c02",
                                        valueFormatter: (value) => `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} signups/day` }]}
                                    margin={{ top: 10, right: 12, bottom: 30, left: 55 }}>
                                    <BarPlot />
                                    <LinePlot />
                                    <MarkPlot />
                                    <ChartsXAxis axisId="signupHour" />
                                    <ChartsYAxis axisId="signupCount" label="Signups" />
                                    <ChartsTooltip trigger="axis" />
                                  </ResponsiveChartContainer>
                                </Box>
                              </Box>
                            </>
                          );
                        })()}
                  </Box>
                )}
                {((canViewSignupAndKpiAnalytics && analyticsView === "kpis") ||
                  (canViewManagementAnalytics && analyticsView === "last7Days")) &&
                  (isLoadingMemberKpis ? (
                    <CircularProgress size={24} />
                  ) : isErrorMemberKpis && !memberKpisData ? (
                    <Typography color="error">
                      Unable to load KYC KPIs.
                    </Typography>
                  ) : (
                    <>
                      <Box
                        aria-hidden={analyticsView !== "kpis"}
                        role="region"
                        aria-label="KYC Operations KPIs"
                        sx={{
                          display: analyticsView === "kpis" ? "grid" : "none",
                          gridTemplateColumns: "repeat(4, minmax(250px, 1fr))",
                          overflowX: "auto",
                          gap: 1,
                          pb: 0.75,
                        }}
                        data-testid="member-kyc-kpi-periods"
                      >
                        {KPI_PERIODS.map(([period, fallback]) => {
                          const title = period === "today" ? dayTitle : fallback;
                          const metric =
                            memberKpisData?.data?.periods?.[period] || {};
                          const total = Number(metric.totalCustomers || 0);
                          const ofacCount = Number(metric.ofacHitCount || 0);
                          const automaticCount = Number(
                            metric.automaticCount || 0
                          );
                          const requiredInterventionCount = Math.max(
                            total - automaticCount,
                            0
                          );
                          const unresolvedCount = Number(
                            metric.unresolvedInterventionCount || 0
                          );
                          const countMetric = (
                            label,
                            count,
                            color,
                            background,
                            isSubMetric = false
                          ) => {
                            const percent = total
                              ? Math.round((count / total) * 100)
                              : 0;
                            return (
                              <Box
                                key={label}
                                sx={
                                  isSubMetric
                                    ? {
                                        ml: 1,
                                        pl: 1,
                                        borderLeft: `2px solid ${KPI_CHART_COLORS.intervention}`,
                                      }
                                    : undefined
                                }
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color={
                                      isSubMetric ? "text.secondary" : undefined
                                    }
                                  >
                                    {label}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={800}
                                    >
                                      {count.toLocaleString()}
                                    </Typography>
                                    <Chip
                                      label={`${percent}%`}
                                      size="small"
                                      sx={{
                                        height: 19,
                                        bgcolor: background,
                                        color,
                                        fontWeight: 700,
                                      }}
                                    />
                                  </Box>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={percent}
                                  sx={{
                                    mt: 0.25,
                                    height: 4,
                                    borderRadius: 2,
                                    bgcolor: background,
                                    "& .MuiLinearProgress-bar": {
                                      bgcolor: color,
                                    },
                                  }}
                                  aria-label={`${title}: ${percent}% ${label.toLowerCase()}`}
                                />
                              </Box>
                            );
                          };
                          const timeMetric = (
                            label,
                            seconds,
                            isCounting = false
                          ) => {
                            const formatted = formatKpiDuration(seconds);
                            const isAvailable = formatted !== "N/A";
                            const isNegative =
                              Number.isSafeInteger(seconds) && seconds < 0;
                            const color = !isAvailable
                              ? "default"
                              : isNegative
                                ? "error"
                                : isCounting
                                  ? "warning"
                                  : "success";
                            const fontWeight = /d$/.test(formatted)
                              ? 800
                              : /h$/.test(formatted)
                                ? 700
                                : /m$/.test(formatted)
                                  ? 600
                                  : 400;
                            return (
                              <Box
                                key={label}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {label}
                                </Typography>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={color}
                                  aria-label={`${label}: ${formatted}`}
                                  icon={
                                    !isAvailable || isCounting || isNegative ? (
                                      <AccessTimeIcon />
                                    ) : (
                                      <CheckCircleOutlineIcon />
                                    )
                                  }
                                  label={formatted}
                                  sx={{
                                    height: 24,
                                    width: 68,
                                    fontSize: 11,
                                    fontWeight,
                                    borderRadius: 1.5,
                                    "& .MuiChip-icon": {
                                      fontSize: 15,
                                      ...(isCounting && {
                                        animation:
                                          "memberElapsedPulse 2s ease-in-out infinite",
                                      }),
                                    },
                                    "& .MuiChip-label": { px: 0.75 },
                                    "@keyframes memberElapsedPulse": {
                                      "0%, 100%": {
                                        opacity: 1,
                                        transform: "scale(1)",
                                      },
                                      "50%": {
                                        opacity: 0.7,
                                        transform: "scale(0.92)",
                                      },
                                    },
                                    "@media (prefers-reduced-motion: reduce)": {
                                      "& .MuiChip-icon": { animation: "none" },
                                    },
                                  }}
                                />
                              </Box>
                            );
                          };
                          return (
                            <Box
                              component="section"
                              aria-label={`${title} KYC KPIs`}
                              key={period}
                              sx={{
                                p: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.65,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1.5,
                                bgcolor: "background.paper",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                  <Button
                                    size="small"
                                    aria-pressed={selectedOrderPeriod === period}
                                    variant={selectedOrderPeriod === period ? "contained" : "text"}
                                    onClick={() => selectOrderPeriod(period)}
                                    sx={{ textTransform: "none", fontWeight: 700 }}
                                  >
                                    {title}
                                  </Button>
                                  {period === "today" && renderPeriodControls()}
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {total.toLocaleString()} customers
                                </Typography>
                              </Box>
                              {[
                                {
                                  key: "automatic",
                                  count: automaticCount,
                                  rows: [
                                    countMetric(
                                      "Automatic",
                                      automaticCount,
                                      KPI_CHART_COLORS.neither,
                                      "#e8f5e9"
                                    ),
                                  ],
                                },
                                {
                                  key: "requiredIntervention",
                                  count: requiredInterventionCount,
                                  rows: [
                                    countMetric(
                                      "Required intervention",
                                      requiredInterventionCount,
                                      KPI_CHART_COLORS.intervention,
                                      "#fff3e0"
                                    ),
                                    countMetric(
                                      "OFAC hit",
                                      ofacCount,
                                      KPI_CHART_COLORS.ofac,
                                      "#ffebee",
                                      true
                                    ),
                                    countMetric(
                                      "Unresolved",
                                      unresolvedCount,
                                      MANUAL_STATUS_COLOR,
                                      "#fff3e0",
                                      true
                                    ),
                                  ],
                                },
                              ]
                                .sort((left, right) => right.count - left.count)
                                .map((group) => (
                                  <Box
                                    key={group.key}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    {group.rows}
                                  </Box>
                                ))}
                              {timeMetric(
                                "Avg. resolution · intervention",
                                metric.averageResolutionSeconds
                              )}
                              {timeMetric(
                                "Avg. first attention · intervention",
                                metric.averageFirstAttentionSeconds
                              )}
                              {timeMetric(
                                "Oldest unresolved · intervention",
                                metric.oldestUnresolvedSeconds,
                                true
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                      <Box
                        component="section"
                        aria-label="New customers by category, last 7 days"
                        sx={{
                          display:
                            analyticsView === "last7Days" ? "block" : "none",
                          mt: 0.75,
                          p: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                          bgcolor: "background.paper",
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          New customers · last 7 days
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Automatic (no OFAC hit, no intervention) plus required
                          intervention equals new customers; OFAC hits are shown
                          in red inside required intervention.
                        </Typography>
                        {(() => {
                          const days =
                            memberKpisData?.data?.lastSevenDays || [];
                          const dates = days.map((day) => {
                            const date = new Date(`${day.date}T12:00:00`);
                            return Number.isNaN(date.getTime())
                              ? day.date
                              : date.toLocaleDateString(undefined, {
                                  weekday: "short",
                                });
                          });
                          const chartSeries = [
                            {
                              type: "bar",
                              data: days.map((day) =>
                                Number(day.neitherCount || 0)
                              ),
                              label: "Automatic",
                              stack: "customers",
                              color: KPI_CHART_COLORS.neither,
                            },
                            {
                              type: "bar",
                              data: days.map((day) =>
                                Number(day.interventionOnlyCount || 0)
                              ),
                              label: "Required intervention · no OFAC",
                              stack: "customers",
                              color: KPI_CHART_COLORS.intervention,
                            },
                            {
                              type: "bar",
                              data: days.map((day) =>
                                Number(day.ofacHitCount || 0)
                              ),
                              label: "Required intervention · OFAC hit",
                              stack: "customers",
                              color: KPI_CHART_COLORS.ofac,
                            },
                            {
                              type: "line",
                              data: days.map((day) =>
                                Number(day.totalCustomers || 0)
                              ),
                              label: "Daily total",
                              color: "#3949ab",
                              curve: "monotoneX",
                              showMark: true,
                            },
                          ];
                          return (
                            <Box
                              data-testid="daily-chart-layout"
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "minmax(0, 1fr)",
                                  sm: "minmax(0, 1fr) 180px",
                                },
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <Box
                                role="group"
                                aria-label="Daily new customer stacked bar chart"
                                data-testid="daily-customer-chart"
                                sx={{ minWidth: 0, mt: 0.5 }}
                              >
                                <ResponsiveChartContainer
                                  height={205}
                                  xAxis={[
                                    {
                                      id: "daily",
                                      scaleType: "band",
                                      data: dates,
                                      tickLabelStyle: { fontSize: 11 },
                                    },
                                  ]}
                                  yAxis={[
                                    {
                                      id: "count",
                                      min: 0,
                                      valueFormatter: (value) =>
                                        Number(value).toLocaleString(),
                                    },
                                  ]}
                                  series={chartSeries}
                                  margin={{
                                    top: 12,
                                    right: 12,
                                    bottom: 28,
                                    left: 38,
                                  }}
                                  sx={{
                                    "& .MuiLineElement-root": {
                                      strokeWidth: 2.5,
                                    },
                                    "& .MuiMarkElement-root": {
                                      stroke: "#fff",
                                      strokeWidth: 2,
                                    },
                                    "& .MuiChartsAxis-line": {
                                      stroke: "rgba(0, 0, 0, 0.18)",
                                    },
                                    "& .MuiChartsAxis-tick": {
                                      stroke: "rgba(0, 0, 0, 0.18)",
                                    },
                                  }}
                                >
                                  <BarPlot />
                                  <LinePlot />
                                  <MarkPlot />
                                  <ChartsXAxis
                                    axisId="daily"
                                    disableLine
                                    disableTicks
                                  />
                                  <ChartsYAxis axisId="count" />
                                  <ChartsTooltip trigger="axis" />
                                </ResponsiveChartContainer>
                              </Box>
                              <Box
                                role="group"
                                aria-label="Daily chart legend"
                                sx={{
                                  display: "flex",
                                  flexDirection: { xs: "row", sm: "column" },
                                  alignItems: {
                                    xs: "center",
                                    sm: "flex-start",
                                  },
                                  justifyContent: {
                                    xs: "center",
                                    sm: "center",
                                  },
                                  flexWrap: { xs: "wrap", sm: "nowrap" },
                                  gap: 0.75,
                                  mt: { xs: 0.5, sm: 0 },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 15,
                                      flexShrink: 0,
                                      borderTop: "2px solid #3949ab",
                                      position: "relative",
                                      "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        width: 5,
                                        height: 5,
                                        borderRadius: "50%",
                                        bgcolor: "white",
                                        border: "1.5px solid #283593",
                                        top: -3.5,
                                        left: 5,
                                      },
                                    }}
                                  />
                                  <Typography variant="caption">
                                    Daily total
                                  </Typography>
                                </Box>
                                {[
                                  ["Automatic", KPI_CHART_COLORS.neither],
                                  [
                                    "Required intervention",
                                    KPI_CHART_COLORS.intervention,
                                  ],
                                  ["OFAC hit", KPI_CHART_COLORS.ofac, true],
                                ].map(([label, color, isSubItem]) => (
                                  <Box
                                    key={label}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      ...(isSubItem && {
                                        ml: { xs: 0, sm: 1 },
                                        pl: { xs: 0, sm: 0.75 },
                                        borderLeft: {
                                          xs: "none",
                                          sm: `2px solid ${KPI_CHART_COLORS.intervention}`,
                                        },
                                      }),
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: 0.5,
                                        bgcolor: color,
                                      }}
                                    />
                                    <Typography variant="caption">
                                      {label}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          );
                        })()}
                      </Box>
                    </>
                  ))}
                {isTotalLoadVisible && (
                  <Box
                    component="section"
                    aria-label={`Net total load, ${loadPeriodTitle.toLowerCase()}`}
                    sx={{
                      p: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Net total load · {loadPeriodTitle}
                          {effectiveStoreId ? ` · store ${effectiveStoreId}` : ""}
                        </Typography>
                        <Tooltip arrow
                          title="Chart shows successful loads minus successful unloads by transaction update date in database time. Month views include every day. All time totals, averages and stores include the full history; only the bars show the current month and preceding 11 months. Total load sums successful loads only; averages divide total or net by the number of successful loads.">
                          <InfoOutlinedIcon data-testid="net-load-info"
                            aria-label="How load volume is calculated"
                            sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
                        </Tooltip>
                      </Box>
                    <Box role="group" aria-label="Load volume period"
                      sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {[
                        ["last7Days", "Last 7 days"],
                        ["thisMonth", "This month"],
                        ["lastMonth", "Last month"],
                        ["allTime", "All time"],
                      ].map(([period, title]) => (
                        <Button key={period} size="small" aria-pressed={loadPeriod === period}
                          variant={loadPeriod === period ? "contained" : "outlined"}
                          onClick={() => selectAnalyticsPeriod(period)} sx={{ textTransform: "none" }}>
                          {title}
                        </Button>
                      ))}
                      {renderPeriodControls()}
                    </Box>
                    </Box>
                    {(() => {
                      if (isLoadingNetLoad) {
                        return <CircularProgress size={24} />;
                      }
                      if (isErrorNetLoad && !netLoadData) {
                        return (
                          <Typography color="error">
                            Unable to load total load.
                          </Typography>
                        );
                      }
                      const days = Array.isArray(netLoadData?.data?.buckets)
                        ? netLoadData.data.buckets
                        : [];
                      const monthly = netLoadData?.data?.bucketUnit === "month";
                      const values = days.map((day) =>
                        Number(day.netLoad || 0)
                      );
                      const totals = netLoadData?.data?.totals;
                      const weekTotal = totals ? Number(totals.netLoad) : values.reduce(
                        (sum, value) => sum + value,
                        0
                      );
                      const grossTotal = totals ? Number(totals.totalLoad) : days.reduce(
                        (sum, day) => sum + Number(day.totalLoad || 0),
                        0
                      );
                      const loadCount = totals ? Number(totals.loadCount) : days.reduce(
                        (sum, day) => sum + Number(day.loadCount || 0),
                        0
                      );
                      const averageDeposit =
                        loadCount > 0 ? grossTotal / loadCount : 0;
                      const averageNetDeposit =
                        loadCount > 0 ? weekTotal / loadCount : 0;
                      const dates = days.map((day) => {
                        const date = new Date(`${day.date}T12:00:00`);
                        return Number.isNaN(date.getTime())
                          ? day.date
                          : date.toLocaleDateString(undefined, {
                              ...(monthly
                                ? { month: "short", year: "2-digit" }
                                : loadPeriod === "last7Days"
                                  ? { weekday: "short" }
                                  : { day: "numeric" }),
                            });
                      });
                      return (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {monthly && "All-time totals · Chart: last 12 months · "}
                            {netLoadData?.data?.startDate} – {netLoadData?.data?.endDate}
                            {monthly ? " · Monthly totals" : " · Daily totals"}
                          </Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                md: "repeat(4, minmax(0, 1fr))",
                              },
                              gap: 0.75,
                              my: 0.75,
                            }}
                          >
                            {[
                              {
                                testId: "net-load-week-total",
                                label: loadPeriod === "last7Days" ? "7-day net total" : loadPeriod === "allTime" ? "All-time net total" : "Period net total",
                                value: formatCurrency(weekTotal),
                                hint: "Loads minus unloads",
                                color: NET_LOAD_COLOR,
                              },
                              {
                                testId: "gross-load-week-total",
                                label: "Total load",
                                value: formatCurrency(grossTotal),
                                hint: "Successful loads only",
                                color: "#2e7d32",
                              },
                              {
                                testId: "average-deposit",
                                label: "Avg deposit (loads)",
                                value: loadCount
                                  ? formatCurrency(averageDeposit)
                                  : "—",
                                hint: `${loadCount} ${
                                  loadCount === 1 ? "load" : "loads"
                                }`,
                                color: "#6a1b9a",
                              },
                              {
                                testId: "average-net-deposit",
                                label: "Avg net deposit",
                                value: loadCount
                                  ? formatCurrency(averageNetDeposit)
                                  : "—",
                                hint: "Net ÷ loads",
                                color: "#ad1457",
                              },
                            ].map((stat) => (
                              <Paper
                                key={stat.testId}
                                variant="outlined"
                                data-testid={stat.testId}
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  borderLeft: `3px solid ${stat.color}`,
                                }}
                              >
                                <Typography
                                  color="text.secondary"
                                  component="div"
                                  sx={{
                                    fontSize: "0.65rem",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.4,
                                    fontWeight: 600,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {stat.label}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 0.75,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "1rem",
                                      fontWeight: 700,
                                      color: stat.color,
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {stat.value}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    {stat.hint}
                                  </Typography>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                          <Box
                            role="group"
                            aria-label={`${monthly ? "Monthly" : "Daily"} net load bar chart`}
                            data-testid="net-load-chart"
                            sx={{ minWidth: 0, mt: 0.5, overflowX: "auto" }}
                          >
                            <Box sx={{ minWidth: days.length > 12 ? days.length * 30 + 90 : 0 }}>
                            <ResponsiveChartContainer
                              height={205}
                              xAxis={[
                                {
                                  id: "loadDay",
                                  scaleType: "band",
                                  data: days.map((day) => day.date),
                                  valueFormatter: (value) =>
                                    dates[days.findIndex((day) => day.date === value)] || value,
                                  tickLabelStyle: { fontSize: 11 },
                                  // -1 overlays the two series in each band:
                                  // total load behind, net load in front.
                                  barGapRatio: -1,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "amount",
                                  valueFormatter: (value) =>
                                    formatCurrency(value),
                                },
                              ]}
                              series={[
                                {
                                  type: "bar",
                                  data: days.map((day) =>
                                    Number(day.totalLoad || 0)
                                  ),
                                  label: "Total load",
                                  color: TOTAL_LOAD_BAR_COLOR,
                                  valueFormatter: (value) =>
                                    formatCurrency(value),
                                },
                                {
                                  type: "bar",
                                  data: values,
                                  label: "Net load",
                                  color: NET_LOAD_COLOR,
                                  valueFormatter: (value) =>
                                    formatCurrency(value),
                                },
                              ]}
                              margin={{
                                top: 12,
                                right: 12,
                                bottom: 28,
                                left: 78,
                              }}
                            >
                              <BarPlot />
                              <ChartsXAxis
                                axisId="loadDay"
                                disableLine
                                disableTicks
                              />
                              <ChartsYAxis axisId="amount" />
                              <ChartsTooltip
                                trigger="axis"
                                slots={{ axisContent: NetLoadTooltipContent }}
                                slotProps={{
                                  axisContent: {
                                    days,
                                    color: NET_LOAD_COLOR,
                                    bucketUnit: monthly ? "month" : "day",
                                  },
                                }}
                              />
                            </ResponsiveChartContainer>
                            </Box>
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                )}
                <Box
                  sx={{
                    display:
                      analyticsView === "distribution" ? "block" : "none",
                  }}
                  aria-hidden={analyticsView !== "distribution"}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, minmax(280px, 1fr))",
                      overflowX: "auto",
                      pb: 0.5,
                      gap: 1,
                    }}
                    data-testid="member-distribution-charts"
                  >
                    {DISTRIBUTION_PERIODS.map(([period, fallback]) => {
                      const periodTitle =
                        period === "today"
                          ? dayTitle
                          : Labels.getLabel(
                              `members.distribution.${period}`,
                              fallback
                            );
                      return (
                        <Box
                          component="section"
                          aria-label={periodTitle}
                          key={period}
                          sx={{
                            p: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.5,
                            bgcolor: "background.paper",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Button
                              fullWidth
                              aria-pressed={selectedOrderPeriod === period}
                              variant={selectedOrderPeriod === period ? "contained" : "text"}
                              onClick={() => selectOrderPeriod(period)}
                              sx={{ textTransform: "none", fontWeight: 700 }}
                            >
                              {periodTitle}
                            </Button>
                            {period === "today" && renderPeriodControls()}
                            {period === "allTime" && (
                              <Tooltip arrow open={distributionInfoOpen}
                                onOpen={() => setDistributionInfoOpen(true)}
                                onClose={() => setDistributionInfoOpen(false)}
                                title={Labels.getLabel(
                                  "members.distribution.scope",
                                  "Periods use employee creation date and database time; weeks start Monday. The calendar selects a shared period for Distribution, KPIs and Load Volume. Click a period heading to select it; the final card shows the chosen day or custom period. All time or clearing the filter resets every tab."
                                )}>
                                <IconButton size="small" aria-label="About distribution periods"
                                  onClick={() => setDistributionInfoOpen((open) => !open)}>
                                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {(() => {
                            if (isLoadingOrderStatusDistribution) {
                              return <CircularProgress size={24} />;
                            }
                            if (
                              !orderStatusDistributionData &&
                              isErrorOrderStatusDistribution
                            ) {
                              return (
                                <Typography color="error">
                                  {Labels.getLabel(
                                    "members.distribution.error",
                                    "Unable to load distribution."
                                  )}
                                </Typography>
                              );
                            }
                            const pieSlices = groupStatisticStatuses(
                              orderStatusDistributionData?.data?.[period] || []
                            )
                              .map((entry) => ({
                                rawStep: entry.status || "UNKNOWN",
                                shortStep: entry.status || "UNKNOWN",
                                count: Number(entry.count || 0),
                                color: getStatusColor(entry.status),
                              }))
                              .filter((entry) => entry.count > 0)
                              .sort((a, b) => b.count - a.count);
                            const total = pieSlices.reduce(
                              (sum, entry) => sum + entry.count,
                              0
                            );

                            if (pieSlices.length === 0) {
                              return (
                                <Typography
                                  color="text.secondary"
                                  sx={{ py: 2 }}
                                >
                                  {Labels.getLabel(
                                    "members.distribution.empty",
                                    "No members created in this period."
                                  )}
                                </Typography>
                              );
                            }

                            const CX = 78;
                            const CY = 78;
                            const R = 56;
                            const IR = 27;
                            let angle = -Math.PI / 2;
                            const slices = pieSlices.map((entry) => {
                              const spanAngle =
                                total > 0
                                  ? (entry.count / total) * 2 * Math.PI
                                  : 0;
                              const startA = angle;
                              angle += spanAngle;
                              const endA = angle;
                              const midA = startA + spanAngle / 2;
                              const large = spanAngle > Math.PI ? 1 : 0;
                              const x1 = CX + R * Math.cos(startA);
                              const y1 = CY + R * Math.sin(startA);
                              const x2 = CX + R * Math.cos(endA);
                              const y2 = CY + R * Math.sin(endA);
                              const ix1 = CX + IR * Math.cos(endA);
                              const iy1 = CY + IR * Math.sin(endA);
                              const ix2 = CX + IR * Math.cos(startA);
                              const iy2 = CY + IR * Math.sin(startA);
                              // A full circle needs two arcs; a single arc has coincident endpoints.
                              const path = (
                                pieSlices.length === 1
                                  ? [
                                      `M ${CX} ${CY - R}`,
                                      `A ${R} ${R} 0 1 1 ${CX} ${CY + R}`,
                                      `A ${R} ${R} 0 1 1 ${CX} ${CY - R}`,
                                      `L ${CX} ${CY - IR}`,
                                      `A ${IR} ${IR} 0 1 0 ${CX} ${CY + IR}`,
                                      `A ${IR} ${IR} 0 1 0 ${CX} ${CY - IR}`,
                                      "Z",
                                    ]
                                  : [
                                      `M ${x1.toFixed(1)} ${y1.toFixed(1)}`,
                                      `A ${R} ${R} 0 ${large} 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`,
                                      `L ${ix1.toFixed(1)} ${iy1.toFixed(1)}`,
                                      `A ${IR} ${IR} 0 ${large} 0 ${ix2.toFixed(1)} ${iy2.toFixed(1)}`,
                                      "Z",
                                    ]
                              ).join(" ");
                              return { ...entry, path, midA };
                            });

                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.75,
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexWrap: "nowrap",
                                  py: 0,
                                }}
                              >
                                <svg
                                  width={112}
                                  height={112}
                                  style={{ flexShrink: 0 }}
                                  viewBox="0 0 156 156"
                                  role="img"
                                  aria-label={`${periodTitle}: ${total} members`}
                                >
                                  {slices.map((slice) => {
                                    const isSelected =
                                      selectedOrderStatus === slice.rawStep &&
                                      selectedOrderPeriod === period;
                                    const isDimmed =
                                      selectedOrderStatus !== null &&
                                      !isSelected;
                                    const tx = isSelected
                                      ? (Math.cos(slice.midA) * 4).toFixed(1)
                                      : "0";
                                    const ty = isSelected
                                      ? (Math.sin(slice.midA) * 4).toFixed(1)
                                      : "0";

                                    return (
                                      <path
                                        key={slice.rawStep}
                                        data-testid={`member-status-slice-${period}-${slice.rawStep}`}
                                        d={slice.path}
                                        fill={
                                          isDimmed ? "#b0bec5" : slice.color
                                        }
                                        opacity={isDimmed ? 0.5 : 0.9}
                                        stroke="white"
                                        strokeWidth="2"
                                        style={{
                                          cursor: "pointer",
                                          transform: `translate(${tx}px, ${ty}px)`,
                                          transition: "transform 0.15s",
                                        }}
                                        onClick={() =>
                                          selectOrderStatus(slice.rawStep, period)
                                        }
                                      />
                                    );
                                  })}
                                  <text
                                    x={CX}
                                    y={CY}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize="22"
                                    fontWeight="bold"
                                    fill="#333"
                                  >
                                    {total}
                                  </text>
                                  <text
                                    x={CX}
                                    y={CY + 24}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize="10"
                                    fill="#777"
                                  >
                                    members
                                  </text>
                                </svg>

                                <Box
                                  role="group"
                                  aria-label={`${periodTitle}: statuses`}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.35,
                                    minWidth: 0,
                                    flex: 1,
                                    maxHeight: 112,
                                    overflowY: "auto",
                                  }}
                                >
                                  {pieSlices.map((slice) => {
                                    const isSelected =
                                      selectedOrderStatus === slice.rawStep &&
                                      selectedOrderPeriod === period;
                                    const isDimmed =
                                      selectedOrderStatus !== null &&
                                      !isSelected;

                                    return (
                                      <Box
                                        component="button"
                                        type="button"
                                        aria-pressed={isSelected}
                                        aria-label={`${periodTitle}: ${slice.shortStep} (${slice.count})`}
                                        key={`legend-${slice.rawStep}`}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          gap: 0.35,
                                          p: 0.3,
                                          borderRadius: 0.75,
                                          border: "1px solid",
                                          borderColor: isSelected
                                            ? "primary.main"
                                            : "divider",
                                          bgcolor: isSelected
                                            ? "action.hover"
                                            : "white",
                                          opacity: isDimmed ? 0.6 : 1,
                                          cursor: "pointer",
                                          minWidth: 105,
                                          flex: "0 0 auto",
                                        }}
                                        onClick={() =>
                                          selectOrderStatus(slice.rawStep, period)
                                        }
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              bgcolor: slice.color,
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 700,
                                              fontSize: "0.72rem",
                                            }}
                                          >
                                            {slice.shortStep}
                                          </Typography>
                                        </Box>
                                        <Typography
                                          variant="caption"
                                          color="text.primary"
                                          sx={{
                                            fontSize: "0.72rem",
                                            fontWeight: 800,
                                          }}
                                        >
                                          {slice.count}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            );
                          })()}
                        </Box>
                      );
                    })}
                  </Box>
                  {selectedOrderStatus === "KYC_MANUAL" && (
                    <Box
                      sx={{
                        mt: 0.6,
                        pt: 0.6,
                        borderTop: "1px dashed",
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.35,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                      >
                        KYC Manual Subfilter
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 0.35, flexWrap: "wrap" }}
                      >
                        {[
                          "RESTRICTED",
                          "HARD_FAIL",
                          "FAILED",
                          "PENDING_SCAN_REVIEW",
                          "DOCS_UPLOADED",
                        ].map((subStatus) => {
                          const count =
                            kycManualSubStatusDistribution.find(
                              (entry) => entry.status === subStatus
                            )?.count || 0;
                          const isSelected =
                            selectedOrderSubStatus === subStatus;
                          return (
                            <Chip
                              key={`kyc-manual-subfilter-${subStatus}`}
                              label={`${subStatus} (${count})`}
                              size="small"
                              color={isSelected ? "warning" : "default"}
                              variant={isSelected ? "filled" : "outlined"}
                              sx={{
                                height: 18,
                                fontSize: "0.68rem",
                                "& .MuiChip-label": {
                                  fontWeight: 800,
                                  color: isSelected
                                    ? "warning.contrastText"
                                    : "text.primary",
                                },
                              }}
                              onClick={() =>
                                setSelectedOrderSubStatus((prev) =>
                                  prev === subStatus ? null : subStatus
                                )
                              }
                            />
                          );
                        })}
                        {selectedOrderSubStatus && (
                          <Chip
                            label="Clear Subfilter"
                            size="small"
                            onClick={() => setSelectedOrderSubStatus(null)}
                            color="primary"
                            variant="outlined"
                            sx={{
                              height: 18,
                              fontSize: "0.68rem",
                              "& .MuiChip-label": {
                                fontWeight: 800,
                                color: "primary.main",
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              {renderStoreChipStrip()}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {refreshError && (
        <MuiAlert severity="error" sx={{ mb: 1 }}>
          {Labels.getLabel(
            "members.refreshError",
            "Unable to update all member information. Displayed data may be out of date; retrying automatically."
          )}
        </MuiAlert>
      )}
      {isLoading || isLoadingDataCount ? (
        <CircularProgress color="inherit" />
      ) : data && dataCount ? (
        <div style={{ height: 670, width: "100%" }}>
          <DataGrid
            rowHeight={56}
            disableSelectionOnClick
            disableColumnFilter
            //disableColumnSelector
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            disableDensitySelector
            {...(page !== undefined ? { page } : {})}
            pageSize={pageSize}
            rowsPerPageOptions={pageSizeOptions}
            onPageSizeChange={(newPageSize) => {
              if (setPageSizeFunction) {
                setPageSizeFunction(newPageSize);
              }
            }}
            onSelectionModelChange={handleRowSelection}
            selectionModel={select}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 800 },
              },
            }}
            rows={data?.data}
            columns={columns}
            rowCount={dataCount?.data}
            onPageChange={(newPage) => {
              setPageFunction(newPage);
            }}
            paginationMode="server"
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={onFilterChange}
            /*sx={{
                      "& .MuiButton-root": {
                        display: "none",
                      }
                    }}*/
            keepNonExistentRowsSelected
          />
          <Snackbar
            open={feedback.open}
            autoHideDuration={6000}
            onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={
                feedback.onClose ? feedback.onClose : handleFeedbackClose
              }
              severity={feedback.type}
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <h4>
          {Labels.getLabel(
            "members.error",
            "An error ocurred, please try again later..."
          )}
        </h4>
      )}
      <Dialog open={loadMonthDialogOpen} onClose={() => setLoadMonthDialogOpen(false)}
        aria-labelledby="member-load-month-title">
        <DialogTitle id="member-load-month-title">Select a month</DialogTitle>
        <DialogContent>
          <TextField autoFocus type="month" label="Month" value={customLoadMonth}
            inputProps={{ min: "0001-01", max: maxMonth }}
            InputLabelProps={{ shrink: true }}
            onChange={(event) => {
              setCustomLoadMonth(event.target.value);
              setLoadMonthError(false);
            }}
            error={loadMonthError}
            helperText={loadMonthError
              ? customLoadMonth > maxMonth ? "Select the current month or an earlier month." : "Select a valid month."
              : "Uses database time. Future months are not supported."}
            sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadMonthDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(customLoadMonth) ||
              Number(customLoadMonth.slice(0, 4)) < 1 ||
              Number(customLoadMonth.slice(0, 4)) >= 9999 ||
              customLoadMonth > maxMonth) {
              setLoadMonthError(true);
              return;
            }
            selectAnalyticsPeriod(customLoadMonth);
            setLoadMonthDialogOpen(false);
          }}>Apply month</Button>
        </DialogActions>
      </Dialog>
      <Menu
        id="member-day-menu"
        anchorEl={dayMenuAnchor}
        open={Boolean(dayMenuAnchor)}
        onClose={() => setDayMenuAnchor(null)}
      >
        <MenuItem selected={creationPeriod === "today"} onClick={() => selectDay("today")}>
          {Labels.getLabel("members.distribution.today", "Today")}
        </MenuItem>
        <MenuItem selected={creationPeriod === "yesterday"} onClick={() => selectDay("yesterday")}>
          {Labels.getLabel("members.distribution.yesterday", "Yesterday")}
        </MenuItem>
        <MenuItem selected={/^\d{4}-\d{2}-\d{2}$/.test(creationPeriod)} onClick={() => {
          setDayMenuAnchor(null);
          setCustomDay(/^\d{4}-\d{2}-\d{2}$/.test(dayPeriod) ? dayPeriod : "");
          setDayError(false);
          setDayDialogOpen(true);
        }}>
          {Labels.getLabel("members.distribution.selectDay", "Select a Day")}
        </MenuItem>
        {[
          ["last7Days", "Last 7 days"],
          ["thisWeek", "This week"],
          ["thisMonth", "This month"],
          ["lastMonth", "Last month"],
        ].map(([period, title]) => (
          <MenuItem key={period} selected={creationPeriod === period}
            onClick={() => selectAnalyticsPeriod(period)}>{title}</MenuItem>
        ))}
        <MenuItem selected={/^\d{4}-\d{2}$/.test(creationPeriod)}
          onClick={openMonthSelection}>Select a month</MenuItem>
        <MenuItem selected={creationPeriod === "allTime"}
          onClick={() => selectAnalyticsPeriod("allTime")}>All time</MenuItem>
      </Menu>
      <Dialog open={dayDialogOpen} onClose={() => setDayDialogOpen(false)} aria-labelledby="member-day-dialog-title">
        <DialogTitle id="member-day-dialog-title">
          {Labels.getLabel("members.distribution.selectDay", "Select a Day")}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="date"
            inputProps={{ min: "0001-01-01", max: maxDay }}
            label={Labels.getLabel("members.distribution.day", "Day")}
            value={customDay}
            onChange={(event) => {
              setCustomDay(event.target.value);
              setDayError(false);
            }}
            InputLabelProps={{ shrink: true }}
            error={dayError}
            helperText={dayError
              ? customDay > maxDay
                ? Labels.getLabel("members.distribution.futureDay", "Select today or an earlier day.")
                : Labels.getLabel("members.distribution.invalidDay", "Select a valid calendar day.")
              : Labels.getLabel("members.distribution.dayHelp", "Uses database time. Future days are not supported.")}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayDialogOpen(false)}>
            {Labels.getLabel("members.distribution.cancel", "Cancel")}
          </Button>
          <Button onClick={applyCustomDay} variant="contained">
            {Labels.getLabel("members.distribution.applyDay", "Apply day")}
          </Button>
        </DialogActions>
      </Dialog>
      {openNewDlg ? (
        <MemberDetail
          open={openNewDlg}
          setOpen={setOpenNewDlg}
          dialogTitle={Labels.getLabel(
            "members.viewMemberInfo",
            "PayAnyDay Info"
          )}
          memberInfo={memberInfo}
        />
      ) : (
        ""
      )}
      {openEditMemberDlg ? (
        <Dialog
          open={openEditMemberDlg}
          maxWidth="md"
          fullWidth
          onClose={() => setOpenEditMemberDlg(false)}
        >
          {/* Title bar with same style as MemberDetail */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "primary.main",
              px: 3,
              minHeight: 52,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" component="div" color="white">
                {Labels.getLabel(
                  "members.changeLocation",
                  "Edit Company/Member Id"
                )}
              </Typography>
              <BusinessIcon sx={{ color: "white" }} />
            </Box>
            <Box>
              <IconButton
                edge="end"
                color="inherit"
                size="small"
                onClick={() => setOpenEditMemberDlg(false)}
                aria-label="close"
              >
                <CloseIcon sx={{ color: "white" }} />
              </IconButton>
            </Box>
          </Box>
          <DialogContent>
            <ModifyCompanyMember
              memberInfo={memberInfo}
              handleClose={() => {
                queryClient.invalidateQueries(["members"]);
                setOpenEditMemberDlg(false);
              }}
            />
          </DialogContent>
        </Dialog>
      ) : (
        ""
      )}
      {openEditEmailDlg ? (
        <Dialog
          open={openEditEmailDlg}
          maxWidth="md"
          fullWidth
          onClose={() => setOpenEditEmailDlg(false)}
        >
          {/* Title bar with same style as MemberDetail */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "primary.main",
              px: 3,
              minHeight: 52,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" component="div" color="white">
                {Labels.getLabel("members.changeEmail", "Edit Username/email")}
              </Typography>
              <EmailIcon sx={{ color: "white" }} />
            </Box>
            <Box>
              <IconButton
                edge="end"
                color="inherit"
                size="small"
                onClick={() => setOpenEditEmailDlg(false)}
                aria-label="close"
              >
                <CloseIcon sx={{ color: "white" }} />
              </IconButton>
            </Box>
          </Box>
          <DialogContent>
            <ModifyEmailMember
              memberInfo={memberInfo}
              handleClose={() => {
                queryClient.invalidateQueries(["members"]);
                setOpenEditEmailDlg(false);
              }}
            />
          </DialogContent>
        </Dialog>
      ) : (
        ""
      )}
      {openSecureInfoDlg ? (
        <MemberSecureInfo
          open={openSecureInfoDlg}
          setOpen={setOpenSecureInfoDlg}
          dialogTitle={Labels.getLabel(
            "members.secureInfo",
            "Secure Member Information"
          )}
          memberInfo={memberInfo}
        />
      ) : (
        ""
      )}
      {openCardActionsDlg ? (
        <ManageCard
          open={openCardActionsDlg}
          setOpen={setOpenCardActionsDlg}
          dialogTitle={Labels.getLabel("members.cardActions", "Card Actions")}
          memberInfo={memberInfo}
        />
      ) : (
        ""
      )}
      {openTransactionsDlg ? (
        <MemberTransactions
          open={openTransactionsDlg}
          setOpen={setOpenTransactionsDlg}
          dialogTitle={Labels.getLabel(
            "members.viewTransactions",
            "Member Transactions"
          )}
          memberInfo={memberInfo}
        />
      ) : (
        ""
      )}
      {openStatementsDlg ? (
        <MemberStatements
          open={openStatementsDlg}
          setOpen={setOpenStatementsDlg}
          memberInfo={memberInfo}
        />
      ) : (
        ""
      )}
    </Fragment>
  );
}
